import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  PaperAirplaneIcon,
  TagIcon,
  EyeIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  BoltIcon,
  PlusIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/solid';
import {
  getPreview,
  getConversations,
  putUpdatePinned,
  putUpdateStatus,
  regenerateDMResponse,
} from '../../api/directMessage';
import useCustomWebSocket from '../../hooks/useWebSocket';
import StatusDropdown from './StatusDropdown';
import DMPreview from './DmPreview';
import DMChat from './DmChat';
import DmFilterBar from './DmFilter';
import './dm.css';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Send } from 'react-bootstrap-icons';
import LoadMoreButton from './LoadMoreButton';
import './dm.css';
import { Message, Conversation } from './dmTypes';
import { useSocial } from '../../context/socialProvider';

const isConversationExpired = (
  conversation: Conversation,
  currentSocial: any
): boolean => {
  if (!conversation?.messages || conversation.messages.length === 0) {
    return false;
  }

  // Find the last message from the user (not from the page/business account)
  const lastUserMessage = conversation.messages
    .slice() // Create a copy to avoid mutating original array
    .reverse() // Start from the most recent message
    .find((message) => message.sender !== currentSocial?.providerName);

  if (!lastUserMessage) {
    return false; // No user message found
  }

  // Calculate if the last user message is over 7 days old
  const lastUserMessageDate = new Date(lastUserMessage.platformCreatedAt);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  return lastUserMessageDate < sevenDaysAgo;
};

const ExpiredConversationWarning = () => (
  <div className="bg-orange-500 bg-opacity-20 border border-orange-500 rounded-lg p-3 mx-4 mb-4">
    <div className="flex items-start">
      <div className="flex-shrink-0"></div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-orange-200">
          Cannot send message
        </h3>
        <div className="mt-1 text-sm text-orange-300">
          <p>
            Facebook does not allow external apps to message conversations where
            the customer's last message is over 7 days old. This is a Facebook
            policy restriction.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const DirectMessagesPage = () => {
  const {
    currentSocial,
    isLoading: isPlatformLoading,
    error: platformError,
  } = useSocial();

  const { sendDirectMessage, requestConversation, lastMessage } =
    useCustomWebSocket();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reply, setReply] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isPublishing, setIsPublishing] = useState(false);
  const [sortOrder, setSortOrder] = useState('DESC');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAdditionalInput, setShowAdditionalInput] = useState(false);
  const [additionalInput, setAdditionalInput] = useState('');
  const [playAudio, setPlayAudio] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const publishingMessageRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const pageSize = 15;

  const focusOnTextarea = () => {
    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(
      textareaRef.current.value.length,
      textareaRef.current.value.length
    );
  };

  const playNotificationSound = () => {
    if (audioRef.current && playAudio) {
      audioRef.current.play().catch((error) => {
        console.log('Error play audio', error);
      });
    }
  };

  const handleEmojiSelect = (emoji: { native: string }) => {
    // Insert emoji at current cursor position or at the end
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newReply =
        reply.substring(0, start) + emoji.native + reply.substring(end);
      setReply(newReply);

      // Set cursor position after the inserted emoji
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + emoji.native.length;
        textarea.selectionEnd = start + emoji.native.length;
      }, 0);
    } else {
      setReply(reply + emoji.native);
    }

    setShowEmojiPicker(false);
  };

  useEffect(() => {
    interface ClickOutsideEvent extends MouseEvent {}

    const handleClickOutside = (event: ClickOutsideEvent): void => {
      // Check if click target is the emoji button
      const target = event.target as Element;
      const isEmojiButton =
        target.closest('button[title="Close emoji picker"]') ||
        target.closest('button[title="Add emoji"]');

      // Only close if:
      // 1. Emoji picker is open AND
      // 2. Click is outside the picker container AND
      // 3. Click is not on the emoji button itself
      if (
        showEmojiPicker &&
        !target.closest('.emoji-picker-container') &&
        !isEmojiButton
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const sortConversations = useCallback(
    (conversations: Conversation[]) => {
      const compareConversations = (
        a: Conversation,
        b: Conversation
      ): number => {
        if (!a || !b) {
          console.error('Undefined conversation in compareConversations', {
            a,
            b,
          });
          return 0;
        }

        const timestampA = new Date(a.lastMessageUpdated).getTime();
        const timestampB = new Date(b.lastMessageUpdated).getTime();

        if (timestampA === timestampB) {
          return b.id - a.id;
        }

        return sortOrder === 'DESC'
          ? timestampB - timestampA
          : timestampA - timestampB;
      };

      const sortedConversations = [...conversations].sort(compareConversations);

      const pinnedConversations = sortedConversations.filter(
        (conv) => conv.pinned
      );
      const unpinnedConversations = sortedConversations.filter(
        (conv) => !conv.pinned
      );

      return [...pinnedConversations, ...unpinnedConversations];
    },
    [sortOrder]
  );

  const updateConversationStatus = useCallback(
    async (convoID: number, newStatus: string) => {
      setConversations((prevConversations) =>
        prevConversations.map((convo) =>
          convo.id === convoID ? { ...convo, status: newStatus } : convo
        )
      );

      try {
        await putUpdateStatus(convoID, newStatus);
      } catch (error) {
        console.error('Failed to update status:', error);
        // Revert the optimistic update
        setConversations((prevConversations) =>
          prevConversations.map((convo) =>
            convo.id === convoID ? { ...convo, status: convo.status } : convo
          )
        );
      }
    },
    []
  );

  const selectConversation = useCallback(
    (platformConvoID: string) => {
      const conversation = conversations.find(
        (convo) => convo.platformConvoID === platformConvoID
      );
      if (conversation) {
        setSelectedConversationId(platformConvoID);

        if (conversation.status === '0') {
          updateConversationStatus(conversation.id, '1');
        }
      }
    },
    [conversations, updateConversationStatus]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAdditionalInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRegenerate();
    }
  };

  const selectedConvo = useMemo(
    () =>
      conversations.find(
        (convo) => convo.platformConvoID === selectedConversationId
      ) || null,
    [conversations, selectedConversationId]
  );

  useEffect(() => {
    if (
      selectedConvo &&
      selectedConvo.messages &&
      selectedConvo.messages.length > 0
    ) {
      const lastMessage =
        selectedConvo.messages[selectedConvo.messages.length - 1];
      setReply(lastMessage.LLMResponse || '');
    } else {
      setReply('');
    }
  }, [selectedConvo]);

  const updatePinned = async (platformConvoID: string, pinned: boolean) => {
    try {
      const conversation = conversations.find(
        (convo) => convo.platformConvoID === platformConvoID
      );
      if (!conversation) return;

      await putUpdatePinned(conversation.id, !pinned);

      setConversations((prevConversations) => {
        const updatedConversations = prevConversations.map((convo) =>
          convo.platformConvoID === platformConvoID
            ? { ...convo, pinned: !pinned }
            : convo
        );
        return sortConversations(updatedConversations);
      });
    } catch (error) {
      console.error('Failed to update pinned status:', error);
    }
  };

  const handleStatusChange = async (newStatus: any) => {
    if (selectedConvo !== null) {
      try {
        await putUpdateStatus(selectedConvo.id, newStatus);
        setConversations((prevConversations) =>
          prevConversations.map((convo) =>
            convo.id === selectedConvo.id
              ? { ...convo, status: newStatus }
              : convo
          )
        );
      } catch (error) {
        console.error('Failed to update status:', error);
      }
    }
  };

  const handleSendMessage = () => {
    if (!currentSocial?.id) return;

    if (isExpired) {
      console.log('Cannot send message: conversation expired');
      return;
    }

    if (reply && selectedConvo !== null && !isPublishing) {
      setIsPublishing(true);
      publishingMessageRef.current = reply;
      console.log('hereee', currentSocial.id);
      sendDirectMessage(reply, selectedConvo.id, currentSocial.id);
      setReply(''); // Clear the reply input after sending
    }
  };

  const loadMoreConversations = async () => {
    if (!currentSocial?.id) return;
    if (page < totalPages) {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      try {
        const conversationsResponse = await getConversations(
          currentSocial.id,
          nextPage,
          pageSize,
          sortOrder,
          statusFilter
        );
        const newConversations = [
          ...conversations,
          ...conversationsResponse.result,
        ];
        const sortedConversations = sortConversations(newConversations);
        setConversations(sortedConversations);
        setPage(nextPage);
      } catch (error) {
        console.error('Failed to load more conversations:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReply(e.target.value);
  };

  useEffect(() => {
    const fetchConversations = async () => {
      if (!currentSocial?.id) return;

      console.log('currentsocial', currentSocial);
      if (currentSocial.id) {
        setIsLoadingPreview(true);
        try {
          const conversationsResponse = await getConversations(
            currentSocial.id,
            1, //page
            pageSize,
            sortOrder,
            statusFilter
          );
          const sortedConversations = sortConversations(
            conversationsResponse.result
          );
          setConversations(sortedConversations);
          setTotalPages(Math.ceil(conversationsResponse.total / pageSize));
        } catch (error) {
          console.error('Failed to fetch conversations:', error);
        } finally {
          setIsLoadingPreview(false);
          setPage(1);
          setSelectedConversationId(null);
        }
      }
    };
    fetchConversations();
  }, [currentSocial?.id, statusFilter, sortOrder]);

  useEffect(() => {
    if (selectedConversationId && conversations.length > 0) {
      const selectedConversationStillExists = conversations.some(
        (conv) => conv.platformConvoID === selectedConversationId
      );

      if (!selectedConversationStillExists) {
        setSelectedConversationId(null);
      }
    }
  }, [conversations, selectedConversationId]);

  useEffect(() => {
    if (!currentSocial?.id) return;
    if (lastMessage !== null) {
      try {
        const { action, payload } = JSON.parse(lastMessage.data);
        console.log(
          'Received WebSocket message:',
          JSON.parse(lastMessage.data)
        );

        switch (action) {
          case 'conversation_details':
            setConversations((prevConversations) => {
              const currentSelectedConvo = prevConversations.find(
                (conv) => conv.platformConvoID === selectedConversationId
              );

              const updatedConversations = prevConversations.filter(
                (convo) => convo.id !== payload.id
              );

              const sortedConversations = sortConversations([
                payload,
                ...updatedConversations,
              ]);

              if (currentSelectedConvo?.id === payload.id) {
                setSelectedConversationId(payload.platformConvoID);
              }

              return sortedConversations;
            });
            return;

          case 'new_message': {
            if (
              payload.sender === currentSocial.providerName &&
              payload.message === publishingMessageRef.current
            ) {
              setIsPublishing(false);
              publishingMessageRef.current = null;
            } else {
              //Play for new incoming messages, not ones you sent
              playNotificationSound();
            }

            const existingConvo = conversations.find(
              (convo) => convo.id === payload.convoID
            );

            if (existingConvo) {
              setConversations((prevConversations) => {
                // store current selected before updating
                const currentSelectedConvo = prevConversations.find(
                  (conv) => conv.platformConvoID === selectedConversationId
                );

                const updatedConversations = sortConversations(
                  prevConversations.map((convo) =>
                    convo.id === payload.convoID
                      ? {
                          ...convo,
                          messages: [...convo.messages, payload],
                          lastMessageUpdated: payload.platformCreatedAt,
                          status:
                            payload.sender !== currentSocial.providerName
                              ? '0'
                              : convo.status,
                        }
                      : convo
                  )
                );

                // If the current selection was moved, update selection to follow it
                if (currentSelectedConvo?.id === payload.convoID) {
                  const updatedConvo = updatedConversations.find(
                    (conv) => conv.id === payload.convoID
                  );
                  if (updatedConvo) {
                    setSelectedConversationId(updatedConvo.platformConvoID);
                  }
                }

                return updatedConversations;
              });
            } else {
              // Request full conversation details
              console.log('requested a conversation');
              requestConversation(payload.convoID);
            }
            return;
          }
          case 'delete_message': {
            setConversations((prevConversations) => {
              return sortConversations(
                prevConversations.map((convo) =>
                  convo.id === payload.convoID
                    ? {
                        ...convo,
                        messages: convo.messages.filter(
                          (msg) => msg.messageID !== payload.messageID
                        ),
                      }
                    : convo
                )
              );
            });
            break;
          }
          default:
            console.warn('Unknown WebSocket action:', action);
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    }
  }, [lastMessage]);

  const handleRegenerate = async () => {
    if (!currentSocial?.id) return;

    if (isRegenerating || !selectedConvo || !selectedConvo.messages.length)
      return;

    let latestMessage =
      selectedConvo.messages[selectedConvo.messages.length - 1];
    console.log('regenerating for message:', latestMessage.message);

    latestMessage = {
      ...latestMessage,
      social: {
        id: String(currentSocial.id),
      },
    };

    setIsRegenerating(true);

    try {
      const response = await regenerateDMResponse(
        latestMessage,
        additionalInput
      );
      console.log('Generated response:', response.generatedMessage);
      if (response) {
        setReply(response.generatedMessage);
      }
    } catch (error) {
      console.error('Failed to regenerate response:', error);
    } finally {
      setIsRegenerating(false);
      setAdditionalInput('');
      setShowAdditionalInput(false);
    }
  };

  const getRegenerateButtonText = () => {
    if (!currentSocial?.id) return;

    if (!selectedConvo || !selectedConvo.messages.length) return 'Generate';

    const latestMessage =
      selectedConvo.messages[selectedConvo.messages.length - 1];
    const isAccountOwnerMessage =
      latestMessage.sender === currentSocial.providerName;

    if (isRegenerating) {
      return isAccountOwnerMessage ? 'Generating...' : 'Regenerating...';
    }
    return isAccountOwnerMessage ? 'Generate' : 'Regenerate';
  };

  const toggleAdditionalInput = () => {
    setShowAdditionalInput(!showAdditionalInput);
  };

  const calculateRows = (text: any) => {
    const lineCount = (text.match(/\n/g) || []).length + 1;
    return Math.min(Math.max(lineCount, 3), 8); // Minimum 3, Maximum 8 rows
  };

  if (!currentSocial) {
    return (
      <div className="flex items-center justify-center h-full text-white text-opacity-50">
        No social platform selected
      </div>
    );
  }

  const isExpired = selectedConvo
    ? isConversationExpired(selectedConvo, currentSocial)
    : false;

  return (
    <div className="h-full flex flex-col">
      <audio
        ref={audioRef}
        src="https://superpower-alpha.s3.amazonaws.com/audio/snip-notification.wav"
      />
      <div className="flex divide-x divide-white divide-opacity-20 h-[calc(100vh-96px)]">
        {/* LEFT COLUMN */}
        <div className="w-[40%] min-w-[240px] max-w-[700px] flex-shrink-0 overflow-y-auto h-full hide-scrollbar">
          <div className="h-28 flex items-center border-b border-white border-opacity-20 text-sm">
            <DmFilterBar
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              playAudio={playAudio}
              setPlayAudio={setPlayAudio}
            />
          </div>
          {isLoadingPreview ? (
            <div className="p-2 flex flex-row hover:bg-white hover:bg-opacity-5 items-center">
              <div className="bg-zinc-800 w-10 h-10 rounded-full object-cover"></div>
              <div className="flex flex-col ml-2 flex-1 min-w-0">
                <div className="bg-zinc-800 h-4 rounded-md w-48 animate-pulse"></div>
                <div className="bg-zinc-800 h-4 mt-1 rounded-md w-48 animate-pulse"></div>
              </div>
            </div>
          ) : (
            <>
              <div className="cursor-pointer">
                {conversations.map((conversation) => (
                  <DMPreview
                    key={conversation.platformConvoID}
                    conversation={conversation}
                    selectConversation={() =>
                      selectConversation(conversation.platformConvoID)
                    }
                    togglePinned={updatePinned}
                    isSelected={
                      selectedConversationId === conversation.platformConvoID
                    }
                    currentSocial={currentSocial}
                  />
                ))}
              </div>
              <div className="flex justify-center items-center mt-8">
                <LoadMoreButton
                  isLoadingMore={isLoadingMore}
                  loadMoreConversations={loadMoreConversations}
                  page={page}
                  totalPages={totalPages}
                />
              </div>
            </>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex-1 min-w-[400px] w-[60%] flex flex-col">
          {selectedConvo === null ? (
            <div className="flex items-center justify-center h-full text-white text-opacity-50">
              Select a conversation to get started
            </div>
          ) : (
            <>
              <div className="h-14 flex items-center border-b border-white border-opacity-20 shrink-0">
                {isLoadingPreview ? (
                  <div className="flex justify-center items-center flex-grow">
                    <div className="loader"></div>
                  </div>
                ) : (
                  <>
                    {selectedConvo ? (
                      <a
                        href={`https://www.instagram.com/${selectedConvo.username}`}
                        className="group relative cursor-pointer"
                        title="View Instagram Profile"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={
                            selectedConvo.profilePicture ||
                            'https://superpower-alpha.s3.amazonaws.com/default_images/no_pfp.jpg'
                          }
                          className="w-10 h-10 rounded-full ml-2 object-cover"
                          alt="Profile"
                        />
                      </a>
                    ) : (
                      <div className="bg-zinc-800 w-10 h-10 rounded-full ml-2 animate-pulse"></div>
                    )}
                    <div className="flex flex-col ml-2 flex-1">
                      <div>
                        {selectedConvo.username || (
                          <div className="bg-zinc-800 h-6 rounded-md w-48 animate-pulse"></div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {!isLoadingPreview && (
                <>
                  <DMChat
                    conversation={selectedConvo}
                    image={selectedConvo.profilePicture}
                    username={selectedConvo.username}
                  />

                  {isExpired && <ExpiredConversationWarning />}
                  <div className="h-58 bg-violet bg-opacity-10 border-t border-white border-opacity-20 shrink-0">
                    <div className="flex flex-col bg-white bg-opacity-10 m-4 rounded-2xl p-4">
                      <textarea
                        rows={calculateRows(reply)}
                        className="w-full text-white bg-white bg-opacity-0 rounded-lg resize-y p-2 text-sm min-h-[72px] max-h-[192px]"
                        value={reply}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        ref={textareaRef}
                        disabled={isRegenerating || isExpired}
                      />
                      <div className="flex flex-row justify-between items-end p-2">
                        <div className="flex items-center">
                          <button
                            className={`flex flex-row items-center ${
                              isRegenerating
                                ? 'text-gray-500 cursor-not-allowed'
                                : 'active:text-yellow text-white'
                            }`}
                            onClick={handleRegenerate}
                            disabled={isRegenerating || isExpired}
                          >
                            {getRegenerateButtonText()}
                            <BoltIcon className="w-4 h-4 ml-1" />
                          </button>
                          <button
                            className={`ml-2 text-white ${
                              isRegenerating
                                ? 'text-gray-500 cursor-not-allowed'
                                : 'active:text-yellow text-white'
                            }`}
                            onClick={toggleAdditionalInput}
                            disabled={isRegenerating || isExpired}
                          >
                            <PlusIcon className="w-4 h-4" />
                          </button>
                          <button
                            className={`ml-2 ${
                              showEmojiPicker
                                ? 'text-yellow'
                                : 'text-white hover:text-yellow'
                            }`}
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            disabled={isExpired}
                            title={
                              showEmojiPicker
                                ? 'Close emoji picker'
                                : 'Add emoji'
                            }
                          >
                            <FaceSmileIcon className="w-4 h-4" />
                          </button>
                          {showEmojiPicker && (
                            <div className="absolute bottom-32 z-50 emoji-picker-container">
                              <Picker
                                data={data}
                                onEmojiSelect={handleEmojiSelect}
                                theme="dark"
                                searchPosition="top"
                                previewPosition="none"
                                skinTonePosition="none"
                                perLine={8}
                                maxFrequentRows={2}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center">
                          <span
                            className={`text-xs mr-3 ${
                              reply.length > 1000
                                ? 'text-red'
                                : 'text-white text-opacity-70'
                            }`}
                          >
                            {reply.length}/1000
                          </span>
                          <button
                            className="flex flex-row items-center text-yellow"
                            onClick={focusOnTextarea}
                          >
                            Edit
                            <PencilSquareIcon className="w-4 h-4 ml-1" />
                          </button>
                        </div>
                      </div>
                      {showAdditionalInput && (
                        <div className="mt-2">
                          <input
                            type="text"
                            value={additionalInput}
                            onChange={(e) => setAdditionalInput(e.target.value)}
                            onKeyDown={handleAdditionalInputKeyDown}
                            placeholder="Add additional context..."
                            className="w-full bg-white bg-opacity-10 text-white rounded-lg p-2 text-sm"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex m-4 gap-2 items-center">
                        <button
                          className="bg-white bg-opacity-10 rounded-full p-2"
                          onClick={() =>
                            updatePinned(
                              selectedConvo.platformConvoID,
                              selectedConvo.pinned
                            )
                          }
                        >
                          <TagIcon
                            className={`w-5 h-5 ${
                              selectedConvo.pinned
                                ? 'text-yellow'
                                : 'text-white'
                            }`}
                          />
                        </button>
                        <StatusDropdown
                          currentStatus={selectedConvo.status}
                          onSelectStatus={handleStatusChange}
                        />
                        <button
                          className={`flex flex-grow flex-row justify-center items-center rounded-2xl py-1 ${
                            isPublishing || reply.length > 1000 || isExpired
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-yellow hover:bg-yellow-600 active:bg-yellow-700'
                          } text-black duration-200
                            ${reply.length > 1000 ? 'opacity-50' : ''}
                          `}
                          onClick={handleSendMessage}
                          disabled={
                            isPublishing || reply.length > 1000 || isExpired
                          }
                        >
                          {isPublishing ? (
                            <>
                              <span className="animate-pulse mr-2">
                                Publishing
                              </span>
                              <div className="w-5 h-5 border-b-2 border-t-2 border-l-2 border-gray-300 rounded-full animate-spin"></div>
                            </>
                          ) : (
                            <>
                              Publish reply
                              <Send className="w-5 h-5 ml-2" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectMessagesPage;
